/**
 * ProductImagesBottomBar - Sticky bar for unsaved changes
 *
 * Shows change summary and save/cancel actions.
 */

import { AlertTriangle, Save } from 'lucide-react'
import { Button } from '@/components/v2/ui'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

interface ProductImagesBottomBarProps {
  hasChanges: boolean
  saving: boolean
  changedChannelKeys: Set<string>
  changedAssetIds: Set<string>
  pendingDeleteCount?: number
  onSave: () => void
  onCancel: () => void
}

export function ProductImagesBottomBar({
  hasChanges, saving, changedChannelKeys, changedAssetIds, pendingDeleteCount = 0, onSave, onCancel,
}: ProductImagesBottomBarProps) {
  return (
    <div className={cn(
      'fixed bottom-0 left-0 right-0 z-50',
      'transform transition-transform duration-300 ease-out',
      hasChanges ? 'translate-y-0' : 'translate-y-full',
    )}>
      <div className="bg-neutral-900 dark:bg-neutral-800 border-t border-neutral-700 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <ChangeSummary channelCount={changedChannelKeys.size} assetCount={changedAssetIds.size} pendingDeleteCount={pendingDeleteCount} />
            <SaveActions saving={saving} onSave={onSave} onCancel={onCancel} />
          </div>
        </div>
      </div>
    </div>
  )
}

function ChangeSummary({ channelCount, assetCount, pendingDeleteCount }: { channelCount: number; assetCount: number; pendingDeleteCount: number }) {
  const { t } = useTranslation()
  const parts: string[] = []
  if (pendingDeleteCount > 0) parts.push(t('productImages.deleteSummary', { count: pendingDeleteCount }))
  if (channelCount > 0) parts.push(t(channelCount === 1 ? 'assets.channelAffected' : 'assets.channelsAffected', { count: channelCount }))
  if (assetCount > 0) parts.push(t(assetCount === 1 ? 'assets.imageChanged' : 'assets.imagesChanged', { count: assetCount }))
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/20">
        <AlertTriangle className="h-5 w-5 text-amber-400" />
      </div>
      <div>
        <p className="text-white font-medium">{t('common.unsavedChanges')}</p>
        <p className="text-neutral-400 text-sm">
          {parts.length > 0 ? parts.join(' \u2022 ') : t('assets.orderChanged')}
        </p>
      </div>
    </div>
  )
}

function SaveActions({ saving, onSave, onCancel }: { saving: boolean; onSave: () => void; onCancel: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3">
      <Button
        variant="ghost"
        onClick={onCancel}
        disabled={saving}
        className="text-neutral-300 hover:text-white hover:bg-neutral-700"
      >
        {t('common.discard')}
      </Button>
      <Button
        variant="primary"
        onClick={onSave}
        disabled={saving}
        className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
      >
        {saving ? (
          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />{t('common.saving')}</>
        ) : (
          <><Save className="h-4 w-4 mr-2" />{t('common.save')}</>
        )}
      </Button>
    </div>
  )
}
